import axios from '@/shared/utils/axios-config';

export type TenantSiteStatus = 'draft' | 'pending_dns' | 'active' | 'disabled';

export interface TenantSite {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  customDomain: string | null;
  dnsVerificationToken: string | null;
  dnsVerifiedAt: string | null;
  brandingOverride: Record<string, any>;
  status: TenantSiteStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSiteInput {
  slug: string;
  name: string;
  customDomain?: string;
  brandingOverride?: Record<string, any>;
}

export interface UpdateSiteInput {
  name?: string;
  brandingOverride?: Record<string, any>;
  status?: TenantSiteStatus;
}

const BASE = '/multi-site/sites';

/**
 * F-builder-wiring (2026-06-11) — la API envuelve TODA respuesta en
 * { success, data, timestamp, ... } (TransformInterceptor global). El
 * interceptor axios del dashboard NO desempaqueta, así que `res.data` es el
 * envelope, no el payload. Sin este unwrap, `list()` retornaba el envelope y
 * `tenantSites.length` era undefined → BuilderOverviewPage caía al CTA
 * "Crear sitio" aunque el tenant SÍ tuviera sitio (bug Watley 2026-06-11).
 * Mismo patrón que pages-api.ts.
 */
interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(payload: Envelope<T> | T): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as any)) {
    const env = payload as Envelope<T>;
    if (env.success === false) {
      throw new Error(env.message ?? 'API error');
    }
    return (env.data ?? (payload as unknown as T)) as T;
  }
  return payload as T;
}

/**
 * Phase-3 SoulBuilder quota state surfaced by GET /multi-site/sites/quota/status.
 * Used by SitesManagementPage to render upgrade banners + disable CTAs.
 */
export interface SoulBuilderQuotaStatus {
  planName: string;
  sitesUsed: number;
  sitesPublished: number;
  sitesDraft: number;
  customDomainsUsed: number;
  maxSitesPublished: number; // -1 = unlimited
  maxSitesDraft: number;
  maxCustomDomains: number;
  effectivePublishedCap: number; // plan cap + extra_site addon qty
  effectiveCustomDomainCap: number;
  extraSiteAddonQty: number;
  extraCustomDomainAddonQty: number;
  canCreateSite: boolean;
  canAttachCustomDomain: boolean;
  upgradeUrl: string;
}

export const multiSiteApi = {
  list: async (): Promise<TenantSite[]> => {
    const res = await axios.get(BASE);
    return unwrap<TenantSite[]>(res.data) ?? [];
  },

  get: async (id: string): Promise<TenantSite> => {
    const res = await axios.get(`${BASE}/${id}`);
    return unwrap<TenantSite>(res.data);
  },

  create: async (input: CreateSiteInput): Promise<TenantSite> => {
    const res = await axios.post(BASE, input);
    return unwrap<TenantSite>(res.data);
  },

  update: async (id: string, input: UpdateSiteInput): Promise<TenantSite> => {
    const res = await axios.patch(`${BASE}/${id}`, input);
    return unwrap<TenantSite>(res.data);
  },

  delete: async (id: string): Promise<void> => {
    await axios.delete(`${BASE}/${id}`);
  },

  verifyDns: async (id: string): Promise<TenantSite> => {
    const res = await axios.post(`${BASE}/${id}/verify-dns`);
    return unwrap<TenantSite>(res.data);
  },

  /**
   * Phase-3 SoulBuilder gating — fetch quota state for upgrade banner.
   * Returns 403 (NO_ACTIVE_SUBSCRIPTION) if no subscription. Treat as "no access".
   */
  getQuotaStatus: async (): Promise<SoulBuilderQuotaStatus> => {
    const res = await axios.get(`${BASE}/quota/status`);
    return unwrap<SoulBuilderQuotaStatus>(res.data);
  },
};
